import * as React from "react";
import { getOrCreateSessionId, resetSessionId } from "../../ApiUtils/session";
import { loadHistory, saveHistory, clearHistory, getLastReportId, setLastReportId } from "../../ApiUtils/history";
import { createChatbotPortalWebSocket, getApplicationPermissions } from "./../../ApiUtils/ApiUtils";
import { getReport, subscribe, ReportRef } from "../../store/reportStore";
import "./ChatbotFloatingWidget.css";

type Props = {
    title?: string;
    icon?: React.ReactNode;
    iconUrl?: string;
    iconSize?: number;
    showIconInHeader?: boolean;
    showIconOnToggle?: boolean;
};

type ChatMessage = {
    id: string;
    isUser: boolean;
    text: string;
    timestamp: number;
    agentName?: string;
    isStreaming?: boolean;
    // "divider" separates the messages of one report from the next; "notice" flags a
    // problem with the current report. Neither is sent to the assistant.
    kind?: "divider" | "notice";
    // On a divider: the report the messages above it belong to.
    fromReportId?: string | null;
};

type WsInbound =
    | { type: "status"; message?: string }
    | { type: "chunk"; content?: string }
    | { type: "complete"; response?: string }
    | { type: "error"; error?: string }
    | Record<string, any>;

function makeId(prefix: string) {
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const UNSUPPORTED_REPORT_MESSAGE =
    "This report doesn't support the AI Assistant feature yet. Please select a compatible report to continue, or contact Support if you need help.";

// The chatbot service answers with a bare "Unknown Report: <id>" when it has no
// configuration for the embedded report. Show the guidance above instead of echoing an
// internal id back at the user. Matched loosely because the reply may arrive wrapped in
// markup, split across frames, or on either the error or the completion path.
function isUnknownReportReply(text: string) {
    return /unknown\s+report\s*:/i.test(text || "");
}

const ChatbotFloatingWidget: React.FC<Props> = (props) => {
    const title = props.title || "AI Assistant";

    const showIconInHeader =
        typeof props.showIconInHeader === "boolean" ? props.showIconInHeader : true;
    const showIconOnToggle =
        typeof props.showIconOnToggle === "boolean" ? props.showIconOnToggle : true;
    const iconSize = typeof props.iconSize === "number" ? props.iconSize : 22;

    const hasCustomIcon = !!props.icon || !!props.iconUrl;

    const [sessionId, setSessionId] = React.useState<string>(() => getOrCreateSessionId());
    const [messages, setMessages] = React.useState<ChatMessage[]>(() =>
        (loadHistory(sessionId) as any[]).map((m: any) => ({
            id: m.id || makeId(m.isUser ? "u" : "a"),
            isUser: !!m.isUser,
            text: String(m.text || ""),
            timestamp: Number(m.timestamp || Date.now()),
            agentName: m.agentName,
            isStreaming: false,
            // "system" is the pre-split kind still sitting in older stored transcripts.
            kind: m.kind === "notice" ? "notice" : m.kind === "divider" || m.kind === "system" ? "divider" : undefined,
            fromReportId: m.fromReportId,
        }))
    );

    const [input, setInput] = React.useState<string>("");
    const [isSending, setIsSending] = React.useState<boolean>(false);
    const [report, setReportState] = React.useState<ReportRef>(() => getReport());
    const [open, setOpen] = React.useState<boolean>(false);
    const [hasUnseenReportChange, setHasUnseenReportChange] = React.useState<boolean>(false);

    const [connStatus, setConnStatus] = React.useState<"Connected" | "Connecting" | "Disconnected">("Connecting");
    const [streamStatus, setStreamStatus] = React.useState<string>("");

    const [permissionLoaded, setPermissionLoaded] = React.useState(false);
    const [hasAiChatbotPermission, setHasAiChatbotPermission] = React.useState(false);

    const listRef = React.useRef<HTMLDivElement>(null);

    const wsRef = React.useRef<WebSocket | null>(null);
    const reconnectTimerRef = React.useRef<number | null>(null);
    const unmountedRef = React.useRef<boolean>(false);

    const currentAssistantIdRef = React.useRef<string | null>(null);
    const currentHtmlRef = React.useRef<string>("");
    const reconnectAttemptRef = React.useRef<number>(0);
    const unknownReportRef = React.useRef<boolean>(false);

    const wsConnected = connStatus === "Connected";

    React.useEffect(() => {
        saveHistory(sessionId, messages as any);
    }, [messages, sessionId]);

    React.useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTo({
                top: listRef.current.scrollHeight,
                behavior: "smooth" as ScrollBehavior,
            });
        }
    }, [messages, isSending]);

    React.useEffect(() => {
        const unsub = subscribe(function (next) {
            setReportState(next);
        });
        setReportState(getReport());
        return unsub;
    }, []);

    // Switching reports invalidates the conversation — every answer in it was about the
    // report the user just left. Wipe the thread, start a fresh backend session, and say
    // why. The stored report id keeps a reload of the same report from clearing anything.
    React.useEffect(() => {
        const nextId = report.id;
        if (!nextId) return;

        const lastId = getLastReportId();
        if (lastId === nextId) return;

        setLastReportId(nextId);

        // Nothing recorded yet means this is the first report we've ever seen, not a switch.
        if (lastId === null) return;

        noteReportChange(lastId, nextId, report.name);
    }, [report.id]);

    // A dashboard whose embed config is broken never reaches the store with an id, so the
    // change effect above stays silent. Say so in the thread instead of leaving the user
    // to wonder why the assistant has nothing to say about what's on screen.
    React.useEffect(() => {
        if (!report.failed) return;

        const alert: ChatMessage = {
            id: makeId("n"),
            isUser: false,
            kind: "notice",
            text: report.name
                ? `${report.name} couldn't be loaded, so the AI Assistant can't answer questions about it. Contact Support if this continues.`
                : "This report couldn't be loaded, so the AI Assistant can't answer questions about it. Contact Support if this continues.",
            timestamp: Date.now(),
        };

        setMessages((prev) => {
            // Clicking through several broken dashboards should leave one notice, not a pile.
            const last = prev[prev.length - 1];
            return last && last.kind === "notice"
                ? prev.slice(0, -1).concat(alert)
                : prev.concat(alert);
        });

        if (!open) setHasUnseenReportChange(true);
    }, [report.failed, report.name]);

    React.useEffect(() => {
        let cancelled = false;

        async function checkPermissions() {
            try {
                const response = await getApplicationPermissions();
                const permissionsArray = response?.applications ?? [];

                let allowed = false;

                permissionsArray.forEach((perm: any) => {
                    if (
                        perm.name === "Chat@prod" ||
                        perm.displayName?.toLowerCase().includes("chatbot")
                    ) {
                        allowed = true;
                    }
                });

                if (!cancelled) {
                    setHasAiChatbotPermission(allowed);
                    setPermissionLoaded(true);
                }
            } catch {
                if (!cancelled) {
                    setHasAiChatbotPermission(false);
                    setPermissionLoaded(true);
                }
            }
        }

        checkPermissions();

        return () => {
            cancelled = true;
        };
    }, []);

    function renderIcon(sz: number) {
        if (props.icon) {
            return (
                <span className="chatbot-icon-box" style={{ ["--icon-size" as any]: `${sz}px` }}>
                    {props.icon}
                </span>
            );
        }
        if (props.iconUrl) {
            return (
                <span className="chatbot-icon-box" style={{ ["--icon-size" as any]: `${sz}px` }}>
                    <img src={props.iconUrl} alt="chatbot icon" className="chatbot-icon-img" />
                </span>
            );
        }
        return null;
    }

    function scheduleReconnect() {
        if (reconnectTimerRef.current) return;

        reconnectAttemptRef.current += 1;
        const delay = Math.min(60000, 5000 * Math.pow(2, reconnectAttemptRef.current - 1));

        reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;
            if (!unmountedRef.current && hasAiChatbotPermission) connectWs();
        }, delay);
    }

    function connectWs() {
        try {
            setConnStatus("Connecting");
            const ws = createChatbotPortalWebSocket();
            wsRef.current = ws;

            ws.onopen = () => {
                setConnStatus("Connected");
                reconnectAttemptRef.current = 0;
            };

            ws.onmessage = (event) => {
                let data: WsInbound;
                try {
                    data = JSON.parse(String(event.data));
                } catch {
                    data = { type: "chunk", content: String(event.data) };
                }
                handleInbound(data);
            };

            ws.onerror = () => {
                setConnStatus("Disconnected");
            };

            ws.onclose = () => {
                setConnStatus("Disconnected");
                scheduleReconnect();
            };
        } catch {
            setConnStatus("Disconnected");
            scheduleReconnect();
        }
    }

    React.useEffect(() => {
        // wait for permission result
        if (!permissionLoaded || !hasAiChatbotPermission) return;

        unmountedRef.current = false;
        connectWs();

        return () => {
            unmountedRef.current = true;

            if (reconnectTimerRef.current) {
                window.clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }

            try {
                wsRef.current?.close();
            } catch {
            }
            wsRef.current = null;
        };
    }, [permissionLoaded, hasAiChatbotPermission]);

    function noteReportChange(prevId: string, nextId: string, nextName: string | null) {
        // The transcript is kept whole and the notice acts as the divider between the two
        // reports, so the user can still read back through what was asked before the
        // switch. Only the backend session rotates, which keeps the assistant from
        // answering about the new report using the previous one's context. Moving the
        // conversation onto the new session id means dropping the old key first —
        // the save effect rewrites the full transcript under the new one.
        clearHistory(sessionId);
        setSessionId(resetSessionId());

        // A trailing divider means the last switch added nothing after it. Reuse its
        // anchor rather than the report we just left, so a run of switches still points
        // at the report the messages above actually belong to.
        const last = messages[messages.length - 1];
        const trailing = last && last.kind === "divider" ? last : null;
        const fromReportId = trailing ? trailing.fromReportId || prevId : prevId;
        const body = trailing ? messages.slice(0, -1) : messages;

        // Nothing above to separate, or the user wandered off and came back without
        // asking anything — in both cases a divider would be claiming a difference that
        // isn't there.
        const showDivider = body.length > 0 && fromReportId !== nextId;

        setMessages(
            showDivider
                ? body.concat({
                    id: makeId("s"),
                    isUser: false,
                    kind: "divider",
                    fromReportId,
                    text: nextName
                        ? `Switched to ${nextName} — messages above were about a different report.`
                        : "Report changed — messages above were about a different report.",
                    timestamp: Date.now(),
                })
                : body
        );

        // If the panel is shut the user can't see the divider, so flag the toggle instead.
        if (showDivider && !open) setHasUnseenReportChange(true);
    }

    function ensureStreamingAssistant() {
        if (currentAssistantIdRef.current) return currentAssistantIdRef.current;

        const id = makeId("a");
        currentAssistantIdRef.current = id;
        currentHtmlRef.current = "";

        setMessages((prev) =>
            prev.concat({
                id,
                isUser: false,
                text: "",
                timestamp: Date.now(),
                agentName: "AI Assistant",
                isStreaming: true,
            })
        );

        return id;
    }

    function updateAssistant(id: string, html: string, isStreaming: boolean) {
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: html, isStreaming } : m)));
    }

    function endStream(finalHtml: string) {
        const id = currentAssistantIdRef.current || ensureStreamingAssistant();
        updateAssistant(id, finalHtml, false);

        currentAssistantIdRef.current = null;
        currentHtmlRef.current = "";
        unknownReportRef.current = false;
        setStreamStatus("");
        setIsSending(false);
    }

    function handleInbound(data: WsInbound) {
        const type = (data as any)?.type;

        if (type === "status") {
            setStreamStatus(typeof (data as any).message === "string" ? (data as any).message : "");
            return;
        }

        if (type === "chunk") {
            // Already swapped in the guidance for this turn; ignore the rest of the reply.
            if (unknownReportRef.current) return;

            const chunk = typeof (data as any).content === "string" ? (data as any).content : "";
            const id = ensureStreamingAssistant();
            currentHtmlRef.current = (currentHtmlRef.current || "") + chunk;

            if (isUnknownReportReply(currentHtmlRef.current)) {
                unknownReportRef.current = true;
                currentHtmlRef.current = UNSUPPORTED_REPORT_MESSAGE;
                updateAssistant(id, UNSUPPORTED_REPORT_MESSAGE, true);
                return;
            }

            updateAssistant(id, currentHtmlRef.current, true);
            return;
        }

        if (type === "complete") {
            const full =
                typeof (data as any).response === "string" && (data as any).response
                    ? (data as any).response
                    : currentHtmlRef.current || "";
            endStream(
                unknownReportRef.current || isUnknownReportReply(full)
                    ? UNSUPPORTED_REPORT_MESSAGE
                    : full
            );
            return;
        }

        if (type === "error") {
            const errText =
                typeof (data as any).error === "string" && (data as any).error
                    ? (data as any).error
                    : "An error occurred.";
            endStream(isUnknownReportReply(errText) ? UNSUPPORTED_REPORT_MESSAGE : errText);
            return;
        }

        const fallback =
            typeof (data as any)?.content === "string"
                ? (data as any).content
                : typeof (data as any)?.response === "string"
                    ? (data as any).response
                    : "";

        if (fallback) {
            if (unknownReportRef.current) return;

            const id = ensureStreamingAssistant();
            currentHtmlRef.current = (currentHtmlRef.current || "") + fallback;

            if (isUnknownReportReply(currentHtmlRef.current)) {
                unknownReportRef.current = true;
                currentHtmlRef.current = UNSUPPORTED_REPORT_MESSAGE;
                updateAssistant(id, UNSUPPORTED_REPORT_MESSAGE, true);
                return;
            }

            updateAssistant(id, currentHtmlRef.current, true);
        }
    }

    async function send() {
        const text = (input || "").trim();
        if (!text || isSending) return;

        if (!report.id) {
            // Echo the question and empty the box the way a real send does. Otherwise the
            // reply reads as an answer to nothing, and the text left sitting in the input
            // adds another identical reply every time the user presses Enter again.
            setMessages((prev) =>
                prev.concat(
                    {
                        id: makeId("u"),
                        isUser: true,
                        text,
                        timestamp: Date.now(),
                    },
                    {
                        id: makeId("a"),
                        isUser: false,
                        text: UNSUPPORTED_REPORT_MESSAGE,
                        timestamp: Date.now(),
                        agentName: "AI Assistant",
                    }
                )
            );
            setInput("");
            return;
        }

        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            setMessages((prev) =>
                prev.concat({
                    id: makeId("a"),
                    isUser: false,
                    text: "Not connected to chat service. Reconnecting…",
                    timestamp: Date.now(),
                    agentName: "AI Assistant",
                })
            );
            scheduleReconnect();
            return;
        }

        setIsSending(true);
        setStreamStatus("");
        unknownReportRef.current = false;

        setMessages((prev) =>
            prev.concat({
                id: makeId("u"),
                isUser: true,
                text,
                timestamp: Date.now(),
            })
        );
        setInput("");

        ensureStreamingAssistant();

        ws.send(
            JSON.stringify({
                action: "sendMessage",
                reportId: report.id,
                sessionId,
                inputText: text,
            })
        );
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    }

    function reset() {
        // Rotate the session as well, so the backend forgets the conversation the user
        // just deleted rather than answering with context they can no longer see.
        clearHistory(sessionId);
        setSessionId(resetSessionId());
        setMessages([]);
    }

    function scrollToBottom(behavior: ScrollBehavior = "auto") {
        const el = listRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior });
    }

    React.useEffect(() => {
        if (!open) return;
        setHasUnseenReportChange(false);
        requestAnimationFrame(() => scrollToBottom("auto"));
    }, [open]);

    if (!permissionLoaded || !hasAiChatbotPermission) {
        return null;
    }

    return (
        <React.Fragment>
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className={"chatbot-toggle" + (isSending ? " chatbot-toggle--busy" : "")}
                    aria-label={hasUnseenReportChange ? "Open chat — report changed, chat was cleared" : "Open chat"}
                    title={hasUnseenReportChange ? "Report changed — chat was cleared" : "Open chat"}
                >
                    {showIconOnToggle !== false && hasCustomIcon ? renderIcon(Math.max(iconSize, 24)) : "Chat"}
                    {isSending && <span className="chatbot-toggle-indicator" aria-hidden="true" />}
                    {!isSending && hasUnseenReportChange && (
                        <span className="chatbot-toggle-pip" aria-hidden="true" />
                    )}
                </button>
            )}

            {open && (
                <div className="chatbot-panel" role="dialog" aria-label="Chatbot">
                    <div className="chatbot-header">
                        <div className="chatbot-header-left">
                            {showIconInHeader !== false && hasCustomIcon && (
                                <span className="chatbot-header-icon">{renderIcon(iconSize)}</span>
                            )}
                            <strong>{title}</strong>
                            <span
                                className={
                                    "chatbot-status-dot " +
                                    (connStatus === "Connected"
                                        ? "connected"
                                        : connStatus === "Connecting"
                                            ? "connecting"
                                            : "disconnected")
                                }
                                aria-label={`${connStatus}`}
                                title={`${connStatus}`}
                            />
                        </div>
                        <div className="chatbot-header-right">
                            <button onClick={reset} className="chatbot-icon-btn" title="Clear chat" aria-label="Clear chat" disabled={isSending}>
                                ↺
                            </button>
                            <button onClick={() => setOpen(false)} className="chatbot-icon-btn" title="Close" aria-label="Close">
                                ✕
                            </button>
                        </div>
                    </div>

                    {report.name && (
                        <div
                            className={"chatbot-scope" + (report.failed ? " chatbot-scope--unavailable" : "")}
                            title={
                                report.failed
                                    ? `${report.name} could not be loaded`
                                    : `Chat is scoped to ${report.name}`
                            }
                        >
                            <span className="chatbot-scope-pin" aria-hidden="true" />
                            <span className="chatbot-scope-text">
                                Report: <strong>{report.name}</strong>
                                {report.failed ? " — unavailable" : ""}
                            </span>
                        </div>
                    )}

                    <div ref={listRef} className="chatbot-messages">
                        {messages.map((m) => (
                            m.kind === "divider" ? (
                                <div key={m.id} className="chatbot-divider" role="status">
                                    <span className="chatbot-divider-label">
                                        <span className="chatbot-divider-icon" aria-hidden="true">⟳</span>
                                        {m.text}
                                    </span>
                                </div>
                            ) : m.kind === "notice" ? (
                                <div key={m.id} className="chatbot-alert" role="status">
                                    <span className="chatbot-alert-icon" aria-hidden="true">!</span>
                                    <span>{m.text}</span>
                                </div>
                            ) : (
                                <div
                                    key={m.id}
                                    className={"chatbot-bubble " + (m.isUser ? "chatbot-bubble--me" : "chatbot-bubble--bot")}
                                >
                                    <div className="chatbot-meta">
                                        <strong>{m.isUser ? "You" : m.agentName || "AI Assistant"}</strong>
                                        <span>{new Date(m.timestamp).toLocaleString()}</span>
                                    </div>
                                    <div>
                                        {!m.isUser ? <div dangerouslySetInnerHTML={{ __html: m.text || "" }} /> : <span>{m.text}</span>}
                                    </div>
                                    {!m.isUser && m.isStreaming && (
                                        <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
                                            {streamStatus ? streamStatus : "Thinking…"}
                                        </div>
                                    )}
                                </div>
                            )
                        ))}
                    </div>

                    <div className="chatbot-disclaimer" role="note" aria-live="polite">
                        AI can make mistakes. Please validate important information.
                    </div>

                    <div className="chatbot-input-row">
                        <textarea
                            className={
                                "chatbot-input" +
                                (isSending || !wsConnected ? " chatbot-input--disabled" : "") +
                                (!wsConnected ? " chatbot-input--offline" : "")
                            }
                            placeholder={!wsConnected ? "Connecting to chat service…" : "Ask a question..."}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            rows={2}
                            disabled={isSending || !wsConnected}
                            aria-disabled={isSending || !wsConnected}
                        />
                        <button
                            className={
                                "chatbot-send-btn" +
                                (isSending ? " chatbot-send-btn--loading" : "") +
                                (!wsConnected ? " chatbot-send-btn--offline" : "")
                            }
                            onClick={send}
                            disabled={isSending || !(input || "").trim() || !wsConnected}
                            aria-busy={isSending}
                            aria-disabled={!wsConnected || isSending}
                            title={!wsConnected ? "Not connected" : "Send"}
                        >
                            {isSending ? (
                                <span className="btn-spinner-wrap">
                                    <span className="btn-spinner" aria-hidden="true" />
                                </span>
                            ) : (
                                "Send"
                            )}
                        </button>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default ChatbotFloatingWidget;